import { useEffect, useRef, useState } from "react";
import { List, X } from "lucide-react";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

const slugify = (text: string, index: number) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || `section-${index}`;

/**
 * Sommaire flottant construit à partir des titres du contenu de l'article.
 * `contentRef` doit pointer sur le conteneur qui reçoit le HTML de l'article.
 */
const BlogToc = ({ contentRef }: { contentRef: React.RefObject<HTMLElement | null> }) => {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const headings = Array.from(root.querySelectorAll("h2, h3")) as HTMLElement[];
    const next: TocItem[] = headings.map((el, i) => {
      const text = el.textContent?.trim() ?? "";
      if (!el.id) el.id = slugify(text, i);
      el.style.scrollMarginTop = "6rem";
      return { id: el.id, text, level: el.tagName === "H2" ? 2 : 3 };
    });
    setItems(next.filter((i) => i.text.length > 0));

    observerRef.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );
    headings.forEach((el) => observer.observe(el));
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [contentRef]);

  // Masque le sommaire flottant dès que le footer devient visible,
  // pour éviter qu'il ne survole et rende le footer illisible.
  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;
    const onIntersect: IntersectionObserverCallback = (entries) => {
      setFooterVisible(entries.some((e) => e.isIntersecting));
    };
    const obs = new IntersectionObserver(onIntersect, {
      rootMargin: "0px 0px -20% 0px",
      threshold: 0,
    });
    obs.observe(footer);
    return () => obs.disconnect();
  }, []);

  if (items.length < 3) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const list = (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.id} className={item.level === 3 ? "pl-4" : ""}>
          <a
            href={`#${item.id}`}
            onClick={(e) => handleClick(e, item.id)}
            className={`block font-body text-sm leading-snug border-l-2 pl-3 py-1 transition-colors ${
              activeId === item.id
                ? "border-accent text-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {item.text}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      {/* Desktop : panneau flottant à gauche de l'article */}
      <nav
        aria-label="Sommaire de l'article"
        aria-hidden={footerVisible}
        className={`hidden xl:block fixed left-[max(1.5rem,calc(50%-38rem))] top-32 w-56 max-h-[70vh] overflow-y-auto z-30 transition-opacity duration-300 ${
          footerVisible ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
          Sommaire
        </p>
        {list}
      </nav>

      {/* Mobile / tablette : bouton flottant + panneau */}
      <div className={`xl:hidden transition-opacity duration-300 ${footerVisible ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Fermer le sommaire" : "Ouvrir le sommaire"}
          className="fixed bottom-24 right-5 z-40 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
        </button>
        {mobileOpen && (
          <nav
            aria-label="Sommaire de l'article"
            className="fixed bottom-40 right-5 z-40 w-[min(20rem,80vw)] max-h-[55vh] overflow-y-auto bg-background border border-border rounded-sm shadow-xl p-5"
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Sommaire
            </p>
            {list}
          </nav>
        )}
      </div>
    </>
  );
};

export default BlogToc;
